// Preferences.tsx
import React from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/accountSection/Preferences";

interface PreferencesProps {
  isEditing: boolean;
  formData: any;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  handleSave: (objectType: any) => void;
  updateState: (newState: any) => void;
}

const Preferences: React.FC<PreferencesProps> = ({
  isEditing,
  formData,
  handleInputChange,
  handleSave,
  updateState,
}) => {
  // Função para salvar automaticamente quando uma preferência é alterada
  const handlePreferenceChange = (field: string, value: any) => {
    const syntheticEvent = {
      target: { value },
    } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

    handleInputChange(syntheticEvent, field);

    // Salva automaticamente após 1 segundo (debounce)
    setTimeout(() => {
      handleSave("preferences");
    }, 1000);
  };

  const handleThemeChange = (theme: string) => {
    handlePreferenceChange("theme", theme);
  };

  const handleToggleChange = (field: string, checked: boolean) => {
    handlePreferenceChange(field, checked);
  };

  return (
    <AlfredoStyles.FieldsContainer>
      {/* Idioma e Região - Sempre editável */}
      <AlfredoStyles.SectionTitle
        style={{ fontSize: "var(--fs-xl)", marginTop: "var(--sp-16)", marginBottom: "var(--sp-20)" }}
      >
        Idioma e Região
      </AlfredoStyles.SectionTitle>

      <AlfredoStyles.InputWrapper>
        <label>Idioma</label>
        <select
          value={formData.language || "pt-BR"}
          onChange={(e) => handlePreferenceChange("language", e.target.value)}
          style={{
            width: "100%",
            padding: "var(--sp-12)",
            border: "1px solid #e1e5e9",
            borderRadius: "8px",
            fontSize: "var(--fs-md)",
            backgroundColor: "white",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <option value="pt-BR">Português (Brasil)</option>
          <option value="en-US">English (US)</option>
          <option value="es-ES">Español</option>
        </select>
      </AlfredoStyles.InputWrapper>

      <AlfredoStyles.InputWrapper>
        <label>Fuso Horário</label>
        <select
          value={formData.timezone || "America/Sao_Paulo"}
          onChange={(e) => handlePreferenceChange("timezone", e.target.value)}
          style={{
            width: "100%",
            padding: "var(--sp-12)",
            border: "1px solid #e1e5e9",
            borderRadius: "8px",
            fontSize: "var(--fs-md)",
            backgroundColor: "white",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <option value="America/Sao_Paulo">Brasília (UTC-3)</option>
          <option value="America/New_York">New York (UTC-5)</option>
          <option value="Europe/London">London (UTC+0)</option>
          <option value="Asia/Tokyo">Tokyo (UTC+9)</option>
        </select>
      </AlfredoStyles.InputWrapper>

      {/* Aparência - Sempre editável */}
      <AlfredoStyles.SectionTitle
        style={{ fontSize: "var(--fs-xl)", marginTop: "var(--sp-32)", marginBottom: "var(--sp-20)" }}
      >
        Aparência
      </AlfredoStyles.SectionTitle>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(calc(var(--u) * 120), 1fr))",
          gap: "var(--sp-16)",
          marginBottom: "var(--sp-24)",
        }}
      >
        {[
          {
            value: "light",
            label: "Claro",
            icon: "☀️",
            description: "Tema claro",
          },
          {
            value: "dark",
            label: "Escuro",
            icon: "🌙",
            description: "Tema escuro",
          },
          {
            value: "auto",
            label: "Automático",
            icon: "⚙️",
            description: "Segue o sistema",
          },
        ].map((theme) => (
          <div
            key={theme.value}
            onClick={() => handleThemeChange(theme.value)}
            style={{
              border: `2px solid ${
                formData.theme === theme.value ? "#0066cc" : "#e1e5e9"
              }`,
              borderRadius: "12px",
              padding: "var(--sp-20) var(--sp-16)",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor:
                formData.theme === theme.value ? "#f0f7ff" : "white",
              transition: "all 0.3s ease",
              transform:
                formData.theme === theme.value ? "scale(1.02)" : "scale(1)",
            }}
            onMouseEnter={(e) => {
              if (formData.theme !== theme.value) {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
            onMouseLeave={(e) => {
              if (formData.theme !== theme.value) {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
          >
            <div style={{ fontSize: "var(--fs-4xl)", marginBottom: "var(--sp-12)" }}>
              {theme.icon}
            </div>
            <div
              style={{ fontWeight: 600, fontSize: "var(--fs-md)", marginBottom: "var(--sp-4)" }}
            >
              {theme.label}
            </div>
            <div style={{ color: "#6c757d", fontSize: "var(--fs-xs)" }}>
              {theme.description}
            </div>
          </div>
        ))}
      </div>

      {/* Notificações - Sempre editável */}
      <AlfredoStyles.SectionTitle
        style={{ fontSize: "var(--fs-xl)", marginTop: "var(--sp-32)", marginBottom: "var(--sp-20)" }}
      >
        Notificações
      </AlfredoStyles.SectionTitle>

      {[
        {
          id: "emailNotifications",
          label: "Notificações por Email",
          description: "Receba atualizações importantes por email",
          icon: "📧",
        },
        {
          id: "pushNotifications",
          label: "Notificações Push",
          description: "Receba notificações em tempo real",
          icon: "🔔",
        },
        {
          id: "marketingEmails",
          label: "Emails de Marketing",
          description: "Receba novidades e ofertas especiais",
          icon: "📢",
        },
      ].map((notification) => (
        <div
          key={notification.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--sp-16)",
            padding: "var(--sp-16)",
            backgroundColor: "#f8f9fa",
            borderRadius: "12px",
            border: "1px solid #e1e5e9",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f1f3f4";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#f8f9fa";
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--sp-16)",
              flex: 1,
            }}
          >
            <div style={{ fontSize: "var(--fs-xl)" }}>{notification.icon}</div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: "var(--sp-4)",
                  fontSize: "calc(var(--u) * 15)",
                }}
              >
                {notification.label}
              </div>
              <div style={{ color: "#6c757d", fontSize: "var(--fs-md)" }}>
                {notification.description}
              </div>
            </div>
          </div>
          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: "calc(var(--u) * 52)",
              height: "calc(var(--u) * 28)",
              flexShrink: 0,
            }}
          >
            <input
              type="checkbox"
              checked={formData[notification.id] || false}
              onChange={(e) =>
                handleToggleChange(notification.id, e.target.checked)
              }
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: "absolute",
                cursor: "pointer",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: formData[notification.id] ? "#0066cc" : "#ccc",
                transition: "0.4s",
                borderRadius: "28px",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  height: "calc(var(--u) * 22)",
                  width: "calc(var(--u) * 22)",
                  left: formData[notification.id] ? "calc(var(--u) * 24)" : "calc(var(--u) * 3)",
                  bottom: "calc(var(--u) * 3)",
                  backgroundColor: "white",
                  transition: "0.4s",
                  borderRadius: "50%",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              />
            </span>
          </label>
        </div>
      ))}

      {/* Informação de salvamento automático */}
      <div
        style={{
          backgroundColor: "#e7f3ff",
          border: "1px solid #b3d9ff",
          borderRadius: "8px",
          padding: "var(--sp-12) var(--sp-16)",
          marginTop: "var(--sp-24)",
          display: "flex",
          alignItems: "center",
          gap: "var(--sp-12)",
        }}
      >
        <div style={{ fontSize: "var(--fs-base)" }}>💾</div>
        <div style={{ fontSize: "var(--fs-md)", color: "#0066cc" }}>
          <strong>Salvamento automático:</strong> Suas preferências são salvas
          automaticamente
        </div>
      </div>
    </AlfredoStyles.FieldsContainer>
  );
};

export default Preferences;
